import { useCallback, useState } from 'react';
import { useErrorHandler } from './useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';

interface UseAsyncOperationOptions<TResult = void> {
  /**
   * Mensagem de sucesso a ser exibida após a operação.
   * Pode ser uma string ou uma função que recebe o resultado e retorna a mensagem.
   * Se não fornecida, nenhuma notificação de sucesso será exibida.
   */
  successMessage?: string | ((result: TResult) => string);
  /**
   * Mensagem de erro padrão caso ocorra um erro.
   * Se não fornecida, será usada a mensagem padrão do handleError.
   */
  errorMessage?: string;
  /**
   * Callback executado quando a operação é bem-sucedida.
   * Recebe o resultado da operação como parâmetro.
   */
  onSuccess?: (result: TResult) => void;
  /**
   * Callback executado quando a operação falha.
   * Recebe o erro como parâmetro.
   */
  onError?: (error: unknown) => void;
  /**
   * Se true, não exibe notificação de erro automaticamente.
   * Útil quando você quer tratar o erro manualmente no onError.
   */
  suppressErrorNotification?: boolean;
}

interface UseAsyncOperationReturn<TResult = void> {
  /**
   * Estado de loading da operação.
   */
  loading: boolean;
  /**
   * Função para executar a operação assíncrona.
   * @param operation - Função assíncrona a ser executada
   */
  execute: <T = TResult>(operation: () => Promise<T>) => Promise<T | undefined>;
  /**
   * Erro ocorrido durante a operação, se houver.
   */
  error: Error | null;
  /**
   * Função para limpar o estado de erro.
   */
  clearError: () => void;
}

/**
 * Hook para gerenciar operações assíncronas com loading, notificações e tratamento de erros.
 * 
 * @example
 * ```tsx
 * const { loading, execute } = useAsyncOperation({
 *   successMessage: 'Operação realizada com sucesso!',
 *   errorMessage: 'Erro ao realizar operação',
 *   onSuccess: (result) => {
 *     console.log('Sucesso!', result);
 *   }
 * });
 * 
 * const handleSave = () => {
 *   execute(async () => {
 *     return await service.save(data);
 *   });
 * };
 * ```
 */
export function useAsyncOperation<TResult = void>(
  options: UseAsyncOperationOptions<TResult> = {}
): UseAsyncOperationReturn<TResult> {
  const {
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    suppressErrorNotification = false,
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { notify } = useAlertContext();
  const { handleError } = useErrorHandler();

  const execute = useCallback(
    async <T = TResult>(operation: () => Promise<T>): Promise<T | undefined> => {
      setLoading(true);
      setError(null);

      try {
        const result = await operation();

        // Exibir mensagem de sucesso se fornecida
        if (successMessage) {
          const message = typeof successMessage === 'function'
            ? successMessage(result as TResult)
            : successMessage;
          notify({
            type: 'success',
            message,
          });
        }

        // Executar callback de sucesso
        if (onSuccess) {
          onSuccess(result as TResult);
        }

        return result;
      } catch (err) {
        const errorInstance = err instanceof Error ? err : new Error(String(err));
        setError(errorInstance);

        // Tratar erro
        if (!suppressErrorNotification) {
          if (errorMessage) {
            handleError(err, errorMessage);
          } else {
            handleError(err);
          }
        }

        // Executar callback de erro
        if (onError) {
          onError(err);
        }

        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [successMessage, errorMessage, onSuccess, onError, suppressErrorNotification, notify, handleError]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    execute,
    error,
    clearError,
  };
}

